// 工事材料検査報告書 — 運用管理系
// 搬入時の検査記録(温湿度・受領者・立会・備考)の集約。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import type { Lookups } from "./helpers";
import { buildLookups, downloadBlob, todayStr, setHeaderRow, fmtDate, fmtTime, round1, uniqueLotIds } from "./helpers";

function resolveTargetWork(db: ExtendedDB, lk: Lookups, workId?: number): ExtendedDB["works"][number] | undefined {
  if (workId != null) {
    return lk.workById.get(workId);
  }
  return db.works.find((w) => w.deleted_at == null);
}

function resolveCarryInLocationName(lk: Lookups, carryInLocationId: number | null | undefined): string {
  if (carryInLocationId != null) {
    return lk.carryInLocationById.get(carryInLocationId)?.name ?? "";
  }
  return "";
}

export async function exportMaterialInspectionReport(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const work = resolveTargetWork(db, lk, workId);

  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";
  const ws = wb.addWorksheet("工事材料検査報告書");

  ws.getCell("A1").value = "工事材料検査報告書";
  ws.getCell("A1").font = { bold: true, size: 14 };
  if (work) {
    ws.getCell("A2").value = "工事名:";
    ws.getCell("A2").font = { bold: true };
    ws.getCell("B2").value = work.official_name;
    ws.getCell("A3").value = "受注者:";
    ws.getCell("A3").font = { bold: true };
    ws.getCell("B3").value = work.contractor_name;
  }
  ws.getCell("A4").value = "出力日:";
  ws.getCell("A4").font = { bold: true };
  ws.getCell("B4").value = todayStr();

  const headers: Array<[string, number]> = [
    ["搬入日", 12], ["搬入時刻", 10], ["搬入場所", 22], ["材料名", 26], ["材料種別", 14],
    ["LotNo", 14], ["管理番号(範囲)", 18], ["予定数量", 10], ["実数量", 10],
    ["気温(℃)", 10], ["湿度(%)", 10], ["受領者", 14], ["立会(施主)", 12], ["立会(受注)", 12],
    ["検査結果", 10], ["備考", 30],
  ];
  const headerRow = 6;
  headers.forEach((h, i) => {
    ws.getCell(headerRow, i + 1).value = h[0];
    ws.getColumn(i + 1).width = h[1];
  });
  setHeaderRow(ws, headerRow);

  const carryIns = db.carryIns
    .filter((c) => c.deleted_at == null && (!work || c.work_id === work.id))
    .filter((c) => c.received_at != null)
    .sort((a, b) => (a.received_at ?? 0) - (b.received_at ?? 0));

  let r = headerRow + 1;
  for (const c of carryIns) {
    const qrs = db.materialQrs.filter((q) => q.carry_in_id === c.id);
    const lotIds = uniqueLotIds(qrs);
    const carryInLocation = resolveCarryInLocationName(lk, c.carry_in_location_id);

    for (const lotId of lotIds) {
      const lot = lk.lotById.get(lotId);
      if (!lot || lot.material_id == null) continue;
      const material = lk.materialById.get(lot.material_id);
      if (!material) continue;
      const details = lk.materialDetailsByMaterialId.get(material.id) ?? [];
      const detailName = details.map((d) => d.detail_name).join("+");
      const lotQrs = qrs.filter((q) => q.lot_id === lotId);
      const ids = lotQrs.map((q) => q.id).sort((a, b) => a - b);

      // received_at は epoch ms (number)。fmtDate/fmtTime は文字列入力を想定しているため
      // 日付は fmtDate(number) で吸収、時刻は ISO 化してから fmtTime に渡す。
      const receivedIso = c.received_at != null ? new Date(c.received_at).toISOString() : "";
      ws.getCell(r, 1).value = fmtDate(c.received_at);
      ws.getCell(r, 2).value = fmtTime(receivedIso);
      ws.getCell(r, 3).value = carryInLocation;
      ws.getCell(r, 4).value = material.name;
      ws.getCell(r, 5).value = detailName;
      ws.getCell(r, 6).value = lot.lot_no ?? "";
      ws.getCell(r, 7).value = ids.length > 0 ? `${ids[0]}-${ids[ids.length - 1]}` : "";
      ws.getCell(r, 8).value = lot.planned_count;
      ws.getCell(r, 9).value = lotQrs.length;
      ws.getCell(r, 10).value = round1(c.temperature);
      ws.getCell(r, 11).value = round1(c.humidity);
      ws.getCell(r, 12).value = c.receiver_name ?? "";
      ws.getCell(r, 13).value = "-";
      ws.getCell(r, 14).value = "-";
      const inspectionOK = lot.planned_count === lotQrs.length;
      ws.getCell(r, 15).value = inspectionOK ? "良" : "要確認";
      ws.getCell(r, 16).value = c.remarks ?? "";
      r++;
    }
  }

  if (carryIns.length === 0) {
    ws.getCell(headerRow + 1, 1).value = "※ 搬入データが存在しません";
    ws.getCell(headerRow + 1, 1).font = { italic: true };
  }

  r += 1;
  ws.getCell(r, 1).value = "集計";
  ws.getCell(r, 1).font = { bold: true };
  r++;
  ws.getCell(r, 1).value = "搬入回数:";
  ws.getCell(r, 2).value = carryIns.length;
  r++;
  const totalLotCount = carryIns.reduce((acc, ci) => acc + uniqueLotIds(db.materialQrs.filter((q) => q.carry_in_id === ci.id)).length, 0);
  ws.getCell(r, 1).value = "搬入ロット数:";
  ws.getCell(r, 2).value = totalLotCount;
  r++;
  const totalQrCount = db.materialQrs.filter((q) => q.carry_in_id != null && (!work || carryIns.some((ci) => ci.id === q.carry_in_id))).length;
  ws.getCell(r, 1).value = "搬入QR数(累計):";
  ws.getCell(r, 2).value = totalQrCount;

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `工事材料検査報告書_${todayStr()}.xlsx`);
}
